// ChatBox.tsx
import { useState, useRef, useEffect } from 'react';
import { Send, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface ChatInfo {
  id: number;
  userId?: number;
  username?: string;
  title: string;
  req: string;
  res: string;
  createdAt: string;
  status: number;
}

interface ChatMessage {
  id: string;
  type: 'req' | 'res';
  content: string;
  timestamp: Date;
  username?: string;
}

interface GroupedChat {
  title: string;
  username: string;
  messages: ChatInfo[];
  lastMessageTime: string;
  previewMessage: string;
}

const ChatBox = () => {
  const { user, chatInfos, isChatLoading } = useAuth();

  const [chatItems, setChatItems] = useState<ChatInfo[]>([]);
  const [groupedChats, setGroupedChats] = useState<GroupedChat[]>([]);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [newChatTitle, setNewChatTitle] = useState('');
  const [newChatMessage, setNewChatMessage] = useState('');
  const [selectedChatTitle, setSelectedChatTitle] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatListRef = useRef<HTMLDivElement>(null);

  // Sync chatInfos to chatItems whenever chatInfos changes
  useEffect(() => {
    if (chatInfos) {
      let chatInfoArray = JSON.parse(chatInfos);
      setChatItems([...chatInfoArray]);
    }
  }, [chatInfos]);

  // Group chat items by title
  useEffect(() => {
    if (chatItems.length > 0) {
      const grouped = chatItems.reduce((acc: { [key: string]: ChatInfo[] }, item) => {
        if (!acc[item.title]) {
          acc[item.title] = [];
        }
        acc[item.title].push(item);
        return acc;
      }, {});

      const groupedArray: GroupedChat[] = Object.entries(grouped).map(([title, messages]) => {
        // Sort messages by creation time (oldest first)
        const sortedMessages = messages.sort((a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );

        return {
          title,
          username: sortedMessages[0].username || 'Unknown',
          messages: sortedMessages,
          lastMessageTime: sortedMessages[sortedMessages.length - 1].createdAt,
          previewMessage: sortedMessages[sortedMessages.length - 1].req
        };
      });

      // Sort grouped chats by last message time (newest first)
      groupedArray.sort((a, b) =>
        new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime()
      );

      setGroupedChats(groupedArray);
    } else {
      setGroupedChats([]);
    }
  }, [chatItems]);

  // Auto-select first chat (most recent)
  useEffect(() => {
    if (groupedChats.length > 0 && !selectedChatTitle) {
      setSelectedChatTitle(groupedChats[0].title);
    }
  }, [groupedChats, selectedChatTitle]);

  // Convert selected chat messages to display format
  useEffect(() => {
    if (selectedChatTitle) {
      const selectedGroup = groupedChats.find(g => g.title === selectedChatTitle);
      if (selectedGroup) {
        const chatMessages: ChatMessage[] = [];

        selectedGroup.messages.forEach((msg) => {
          // Add request message
          chatMessages.push({
            id: `${msg.id}-req`,
            type: 'req',
            content: msg.req,
            timestamp: new Date(msg.createdAt),
            username: msg.username
          });

          // Add response message if exists
          if (msg.res && msg.res.trim()) {
            chatMessages.push({
              id: `${msg.id}-res`,
              type: 'res',
              content: msg.res,
              timestamp: new Date(new Date(msg.createdAt).getTime() + 1000)
            });
          }
        });

        setMessages(chatMessages);
      }
    }
  }, [selectedChatTitle, groupedChats]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleCreateNewChat = async () => {
    if (newChatTitle.trim() && newChatMessage.trim()) {
      setIsLoading(true);
      try {
        const response = await fetch("https://user-api.u-slot.vip/api/Alarm/comment_req", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            title: newChatTitle.trim(),
            req: newChatMessage.trim(),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          console.log("Sent Message Failed!", "error");
          setIsLoading(false);
          return;
        }

        if (data && data.code === 0) {
          console.log("Thank you for reaching out. An admin will respond shortly.", "success");
          const newChat: ChatInfo = {
            id: Date.now(),
            userId: user?.id,
            username: user?.username,
            title: newChatTitle,
            req: newChatMessage,
            res: '',
            createdAt: new Date().toISOString(),
            status: 1
          };
          setChatItems([...chatItems, newChat]);
          setSelectedChatTitle(newChatTitle);
        } else {
          console.log("Sent Message Failed!", "error");
        }
      } catch (err) {
        console.log(err instanceof Error ? err.message : 'An error occurred', "error");
      } finally {
        setIsLoading(false);
      }

      setShowNewChatModal(false);
      setNewChatTitle('');
      setNewChatMessage('');
    }
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() && selectedChatTitle) {
      setIsLoading(true);
      try {
        const response = await fetch("https://user-api.u-slot.vip/api/Alarm/comment_req", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            title: selectedChatTitle,
            req: newMessage.trim(),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          console.log("Sent Message Failed!", "error");
          setIsLoading(false);
          return;
        }

        if (data && data.code === 0) {
          console.log("Message sent successfully!", "success");
          const message: ChatMessage = {
            id: `${Date.now()}`,
            type: 'req',
            content: newMessage,
            timestamp: new Date(),
            username: 'You'
          };

          setMessages([...messages, message]);
        } else {
          console.log("Sent Message Failed!", "error");
        }
      } catch (err) {
        console.log(err instanceof Error ? err.message : 'An error occurred', "error");
      } finally {
        setIsLoading(false);
      }
      setNewMessage('');
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="chatbox-container">
      {showNewChatModal && (
        <div className="chatbox-modal-overlay">
          <div className="chatbox-modal-content">
            <h2 className="chatbox-modal-title">Start New Chat</h2>

            <div className="chatbox-modal-form">
              <div className="chatbox-form-group">
                <label className="chatbox-form-label">Chat Title</label>
                <input
                  type="text"
                  value={newChatTitle}
                  onChange={(e) => setNewChatTitle(e.target.value)}
                  placeholder="Enter chat title..."
                  className="chatbox-form-input"
                />
              </div>

              <div className="chatbox-form-group">
                <label className="chatbox-form-label">Your Message</label>
                <textarea
                  value={newChatMessage}
                  onChange={(e) => setNewChatMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={4}
                  className="chatbox-form-textarea"
                />
              </div>

              <div className="chatbox-modal-buttons">
                <button
                  onClick={() => {
                    setShowNewChatModal(false);
                    setNewChatTitle('');
                    setNewChatMessage('');
                  }}
                  className="chatbox-btn-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateNewChat}
                  disabled={!newChatTitle.trim() || !newChatMessage.trim() || isLoading}
                  className="chatbox-btn-primary"
                >
                  {isLoading ? <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Starting...
                  </span> : 'Start Chat'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isChatLoading ?
        (<div className="w-full flex items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-white rounded-full animate-spin"></div>
        </div>)
        : (
          <>
            <div className="chatbox-sidebar">
              <div className="chatbox-sidebar-header">
                <div className="chatbox-sidebar-header-top">
                  <h2 className="chatbox-sidebar-title">Messages</h2>
                  <button
                    onClick={() => setShowNewChatModal(true)}
                    className="chatbox-btn-new-chat"
                  >
                    + New Chat
                  </button>
                </div>
                <p className="chatbox-sidebar-subtitle">{chatItems.length} conversations</p>
              </div>

              <div ref={chatListRef} className="chatbox-chat-list">
                {groupedChats.length === 0 ? (
                  <div className="chatbox-chat-list-empty">
                    <div className="chatbox-empty-icon">
                      <Send size={28} />
                    </div>
                    <h3 className="chatbox-empty-title">No conversations yet</h3>
                    <p className="chatbox-empty-text">Start a new chat to begin messaging</p>
                    <button
                      onClick={() => setShowNewChatModal(true)}
                      className="chatbox-btn-start-chat"
                    >
                      Start New Chat
                    </button>
                  </div>
                ) : (
                  groupedChats
                    .map((chat) => (
                      <div
                        key={chat.title}
                        onClick={() => setSelectedChatTitle(chat.title)}
                        className={`chatbox-chat-item ${selectedChatTitle === chat.title ? 'chatbox-chat-item-active' : ''}`}
                      >
                        <div className="chatbox-chat-item-content">
                          <div className="chatbox-chat-avatar">
                            {chat.username.charAt(0)}
                          </div>
                          <div className="chatbox-chat-item-details">
                            <div className="chatbox-chat-item-header">
                              <h3 className="chatbox-chat-username">{chat.username}</h3>
                              <span className="chatbox-chat-time">
                                {formatTime(new Date(chat.lastMessageTime))}
                              </span>
                            </div>
                            <p className="chatbox-chat-title">{chat.title}</p>
                            <p className="chatbox-chat-preview">{chat.previewMessage}</p>
                            <div className="flex" style={{ textAlign: 'end' }}>
                              <span className="chatbox-chat-preview">
                                {chat.messages.length} messages
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>

            <div className="chatbox-main">
              {selectedChatTitle ? (
                <>
                  <div className="chatbox-header">
                    <div className="chatbox-header-content">
                      <button
                        onClick={() => setSelectedChatTitle(null)}
                        className="chatbox-btn-back"
                      >
                        <ArrowLeft size={20} />
                      </button>
                      <div className="chatbox-header-avatar">
                        {groupedChats.find(c => c.title === selectedChatTitle)?.username.charAt(0)}
                      </div>
                      <div className="chatbox-header-info">
                        <h3>{groupedChats.find(c => c.title === selectedChatTitle)?.username}</h3>
                        <p>{selectedChatTitle}</p>
                      </div>
                    </div>
                  </div>

                  <div className="chatbox-messages-container">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`chatbox-message-wrapper ${message.type === 'req' ? 'chatbox-message-sent' : 'chatbox-message-received'}`}
                      >
                        <div className={`chatbox-message-bubble ${message.type === 'req' ? 'chatbox-message-bubble-sent' : 'chatbox-message-bubble-received'}`}>
                          {message.type === 'req' && message.username && (
                            <p className="chatbox-message-username">{message.username}</p>
                          )}
                          <p className="chatbox-message-content">{message.content}</p>
                          <p className="chatbox-message-time">
                            {formatTime(message.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="chatbox-input-container">
                    <div className="chatbox-input-wrapper">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Type your message..."
                        className="chatbox-input"
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || isLoading}
                        className="chatbox-btn-send"
                      >
                        {isLoading ? (<i className="ri-loader-4-line animate-spin"></i>) : (<Send size={20} />)}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="chatbox-empty-state">
                  <div className="chatbox-empty-content">
                    <div className="chatbox-empty-chat-icon">
                      <Send size={32} />
                    </div>
                    <h3 className="chatbox-empty-chat-title">
                      {chatItems.length === 0 ? 'Start Your First Chat' : 'Select a conversation'}
                    </h3>
                    <p className="chatbox-empty-chat-text">
                      {chatItems.length === 0
                        ? 'Create a new chat to start messaging with admin'
                        : 'Choose a chat from the sidebar to start messaging'}
                    </p>
                    {chatItems.length === 0 && (
                      <button
                        onClick={() => setShowNewChatModal(true)}
                        className="chatbox-btn-empty-start"
                      >
                        Start New Chat
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>)}
      <style>{`
            /* Main Container */
            .chatbox-container {
                display: flex;
                height: 90vh;
                background: linear-gradient(to bottom right, #4c1d95, #6b21a8, #4338ca);
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            }

            /* Modal Overlay */
            .chatbox-modal-overlay {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.6);
                backdrop-filter: blur(8px);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 50;
                padding: 1rem;
            }

            /* Modal Content */
            .chatbox-modal-content {
                background: linear-gradient(to bottom right, #6b21a8, #581c87);
                border-radius: 1rem;
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                max-width: 28rem;
                width: 100%;
                padding: 1.5rem;
                border: 1px solid rgba(147, 51, 234, 0.3);
            }

            .chatbox-modal-title {
                font-size: 1.5rem;
                font-weight: 700;
                color: white;
                margin: 0 0 1rem 0;
            }

            .chatbox-modal-form {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            .chatbox-form-group {
                display: flex;
                flex-direction: column;
            }

            .chatbox-form-label {
                display: block;
                color: #e9d5ff;
                font-size: 0.875rem;
                font-weight: 500;
                margin-bottom: 0.5rem;
            }

            .chatbox-form-input {
                width: 100%;
                background-color: rgba(88, 28, 135, 0.5);
                color: white;
                border-radius: 0.5rem;
                padding: 0.75rem 1rem;
                border: none;
                outline: none;
                font-size: 1rem;
            }

            .chatbox-form-input:focus {
                outline: 2px solid #a855f7;
                outline-offset: 2px;
            }

            .chatbox-form-input::placeholder {
                color: #c084fc;
            }

            .chatbox-form-textarea {
                width: 100%;
                background-color: rgba(88, 28, 135, 0.5);
                color: white;
                border-radius: 0.5rem;
                padding: 0.75rem 1rem;
                border: none;
                outline: none;
                resize: none;
                font-size: 1rem;
                font-family: inherit;
            }

            .chatbox-form-textarea:focus {
                outline: 2px solid #a855f7;
                outline-offset: 2px;
            }

            .chatbox-form-textarea::placeholder {
                color: #c084fc;
            }

            .chatbox-modal-buttons {
                display: flex;
                gap: 0.75rem;
                padding-top: 0.5rem;
            }

            .chatbox-btn-cancel {
                flex: 1;
                background-color: rgba(126, 34, 206, 0.5);
                color: white;
                padding: 0.75rem 1rem;
                border-radius: 0.5rem;
                border: none;
                font-weight: 500;
                cursor: pointer;
                transition: background-color 0.2s;
                font-size: 1rem;
            }

            .chatbox-btn-cancel:hover {
                background-color: rgba(126, 34, 206, 0.7);
            }

            .chatbox-btn-primary {
                flex: 1;
                background: linear-gradient(to right, #a855f7, #ec4899);
                color: white;
                padding: 0.75rem 1rem;
                border-radius: 0.5rem;
                border: none;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s;
                font-size: 1rem;
            }

            .chatbox-btn-primary:hover {
                background: linear-gradient(to right, #9333ea, #db2777);
            }

            .chatbox-btn-primary:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }

            /* Sidebar */
            .chatbox-sidebar {
                width: 40%;
                max-width: 20rem;
                background-color: rgba(107, 33, 168, 0.3);
                backdrop-filter: blur(8px);
                border-right: 1px solid rgba(147, 51, 234, 0.3);
                display: flex;
                flex-direction: column;
            }

            .chatbox-sidebar-header {
                padding: 1rem;
                border-bottom: 1px solid rgba(147, 51, 234, 0.3);
            }

            .chatbox-sidebar-header-top {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 0.5rem;
            }

            .chatbox-sidebar-title {
                font-size: 1.25rem;
                font-weight: 600;
                color: white;
                margin: 0;
                line-height: 15px;
            }

            .chatbox-btn-new-chat {
                background: linear-gradient(to right, #a855f7, #ec4899);
                color: white;
                padding: 0.5rem 1rem;
                border-radius: 0.5rem;
                border: none;
                font-size: 0.875rem;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s;
            }

            .chatbox-btn-new-chat:hover {
                background: linear-gradient(to right, #9333ea, #db2777);
            }

            .chatbox-sidebar-subtitle {
                font-size: 0.875rem;
                color: #e9d5ff;
                margin: 0;
            }

            /* Chat List */
            .chatbox-chat-list {
                flex: 1;
                overflow-y: auto;
            }

            .chatbox-chat-list-empty {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 1.5rem;
                text-align: center;
            }

            .chatbox-empty-icon {
                width: 4rem;
                height: 4rem;
                background-color: rgba(126, 34, 206, 0.3);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 1rem;
                color: #d8b4fe;
            }

            .chatbox-empty-title {
                color: white;
                font-weight: 600;
                margin: 0 0 0.5rem 0;
                font-size: 1rem;
            }

            .chatbox-empty-text {
                color: #d8b4fe;
                font-size: 0.875rem;
                margin: 0 0 1rem 0;
            }

            .chatbox-btn-start-chat {
                background: linear-gradient(to right, #a855f7, #ec4899);
                color: white;
                padding: 0.5rem 1.5rem;
                border-radius: 0.5rem;
                border: none;
                font-size: 0.875rem;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s;
            }

            .chatbox-btn-start-chat:hover {
                background: linear-gradient(to right, #9333ea, #db2777);
            }

            /* Chat Item */
            .chatbox-chat-item {
                padding: 1rem;
                cursor: pointer;
                border-bottom: 1px solid rgba(147, 51, 234, 0.2);
                transition: background-color 0.2s;
            }

            .chatbox-chat-item:hover {
                background-color: rgba(126, 34, 206, 0.3);
            }

            .chatbox-chat-item-active {
                background-color: rgba(126, 34, 206, 0.4);
            }

            .chatbox-chat-item-content {
                display: flex;
                align-items: flex-start;
                gap: 0.75rem;
            }

            .chatbox-chat-avatar {
                width: 2.5rem;
                height: 2.5rem;
                border-radius: 50%;
                background: linear-gradient(to bottom right, #c084fc, #f9a8d4);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: 600;
                flex-shrink: 0;
            }

            .chatbox-chat-item-details {
                flex: 1;
                min-width: 0;
            }

            .chatbox-chat-item-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
            }

            .chatbox-chat-username {
                color: white;
                font-weight: 500;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                margin: 0;
                font-size: 1rem;
                line-height: 22px;
            }

            .chatbox-chat-time {
                font-size: 0.75rem;
                color: #d8b4fe;
                margin-left: 0.5rem;
                flex-shrink: 0;
            }

            .chatbox-chat-title {
                font-size: 0.875rem;
                color: #e9d5ff;
                font-weight: 500;
            }

            .chatbox-chat-preview {
                font-size: 0.875rem;
                color: #d8b4fe;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            /* Main Chat Area */
            .chatbox-main {
                flex: 1;
                display: flex;
                flex-direction: column;
            }

            /* Chat Header */
            .chatbox-header {
                background-color: rgba(107, 33, 168, 0.4);
                backdrop-filter: blur(8px);
                border-bottom: 1px solid rgba(147, 51, 234, 0.3);
                padding: 1rem;
            }

            .chatbox-header-content {
                display: flex;
                align-items: center;
                gap: 0.75rem;
            }

            .chatbox-btn-back {
                color: white;
                background-color: transparent;
                border: none;
                padding: 0.5rem;
                border-radius: 0.5rem;
                cursor: pointer;
                transition: background-color 0.2s;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .chatbox-btn-back:hover {
                background-color: rgba(126, 34, 206, 0.3);
            }

            @media (min-width: 1024px) {
                .chatbox-btn-back {
                    display: none;
                }
            }

            .chatbox-header-avatar {
                width: 2.5rem;
                height: 2.5rem;
                border-radius: 50%;
                background: linear-gradient(to bottom right, #c084fc, #f9a8d4);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: 600;
            }

            .chatbox-header-info h3 {
                color: white;
                font-weight: 600;
                margin: 0;
                font-size: 1rem;
                line-height: 22px;
            }

            .chatbox-header-info p {
                font-size: 0.875rem;
                color: #d8b4fe;
                margin: 0;
            }

            /* Messages Area */
            .chatbox-messages-container {
                flex: 1;
                overflow-y: auto;
                padding: 1rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            .chatbox-message-wrapper {
                display: flex;
            }

            .chatbox-message-sent {
                justify-content: flex-end;
            }

            .chatbox-message-received {
                justify-content: flex-start;
            }

            .chatbox-message-bubble {
                max-width: 28rem;
                padding: 10px;
                border-radius: 1rem;
            }

            .chatbox-message-bubble-sent {
                background: linear-gradient(to right, #a855f7, #ec4899);
                color: white;
                border-bottom-right-radius: 0.25rem;
            }

            .chatbox-message-bubble-received {
                background-color: rgba(183, 160, 248, 0.349);
                backdrop-filter: blur(8px);
                color: white;
                border-bottom-left-radius: 0.25rem;
            }

            .chatbox-message-username {
                font-size: 0.75rem;
                opacity: 0.8;
            }

            .chatbox-message-content {
                white-space: pre-wrap;
                word-wrap: break-word;
                margin: 0;
                font-size: 15px;
            }

            .chatbox-message-time {
                font-size: 0.75rem;
                opacity: 0.7;
                text-align: right;
            }

            /* Chat Input */
            .chatbox-input-container {
                background-color: rgba(107, 33, 168, 0.4);
                backdrop-filter: blur(8px);
                border-top: 1px solid rgba(147, 51, 234, 0.3);
                padding: 1rem;
            }

            .chatbox-input-wrapper {
                display: flex;
                align-items: center;
                gap: 0.5rem;
            }

            .chatbox-input {
                flex: 1;
                background-color: rgba(88, 28, 135, 0.5);
                color: white;
                border-radius: 9999px;
                padding: 0.75rem 1rem;
                border: none;
                outline: none;
                font-size: 1rem;
            }

            .chatbox-input:focus {
                outline: 2px solid #a855f7;
                outline-offset: 2px;
            }

            .chatbox-input::placeholder {
                color: #c084fc;
            }

            .chatbox-btn-send {
                background: linear-gradient(to right, #a855f7, #ec4899);
                color: white;
                padding: 0.75rem;
                border-radius: 50%;
                border: none;
                cursor: pointer;
                transition: all 0.2s;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 3rem;
                height: 3rem;
            }

            .chatbox-btn-send:hover {
                background: linear-gradient(to right, #9333ea, #db2777);
            }

            .chatbox-btn-send:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }

            /* Empty Chat State */
            .chatbox-empty-state {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .chatbox-empty-content {
                text-align: center;
            }

            .chatbox-empty-chat-icon {
                width: 5rem;
                height: 5rem;
                background-color: rgba(126, 34, 206, 0.3);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 0 auto 1rem;
                color: #d8b4fe;
            }

            .chatbox-empty-chat-title {
                font-size: 1.25rem;
                color: white;
                font-weight: 600;
                margin: 0 0 0.5rem 0;
            }

            .chatbox-empty-chat-text {
                color: #d8b4fe;
                margin: 0 0 1rem 0;
            }

            .chatbox-btn-empty-start {
                background: linear-gradient(to right, #a855f7, #ec4899);
                color: white;
                padding: 0.75rem 1.5rem;
                border-radius: 0.5rem;
                border: none;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s;
                font-size: 1rem;
            }

            .chatbox-btn-empty-start:hover {
                background: linear-gradient(to right, #9333ea, #db2777);
            }

            /* Scrollbar Styles */
            .chatbox-chat-list::-webkit-scrollbar,
            .chatbox-messages-container::-webkit-scrollbar {
                width: 6px;
            }

            .chatbox-chat-list::-webkit-scrollbar-track,
            .chatbox-messages-container::-webkit-scrollbar-track {
                background: rgba(88, 28, 135, 0.3);
            }

            .chatbox-chat-list::-webkit-scrollbar-thumb,
            .chatbox-messages-container::-webkit-scrollbar-thumb {
                background: rgba(168, 85, 247, 0.5);
                border-radius: 3px;
            }

            .chatbox-chat-list::-webkit-scrollbar-thumb:hover,
            .chatbox-messages-container::-webkit-scrollbar-thumb:hover {
                background: rgba(168, 85, 247, 0.7);
            }
          `}</style>
    </div>
  );
};

export default ChatBox;